import type { ParentCategory } from '@/types';
import type { SetSpaceBudgetRequest, SpaceBudget } from '@/types/spaces';

export const MAX_BUDGET_LINES = 50;
export const UNCATEGORIZED_LABEL = 'Sem categoria compartilhada';

export interface BudgetCategory { id: string; label: string; icon: string | null; parentLabel: string | null }

// The shared taxonomy, flattened for a picker and a lookup: roots first, each followed by its children. Only these
// can carry a line: a personal custom category belongs to one person and never reaches a space's budget.
export function budgetCategories(tree: ParentCategory[] | undefined): BudgetCategory[] {
  return (tree ?? []).flatMap((parent) => [
    { id: parent.id, label: parent.descriptionTranslated, icon: parent.icon, parentLabel: null },
    ...parent.children.map((child) => ({ id: child.id, label: child.descriptionTranslated, icon: child.icon ?? parent.icon, parentLabel: parent.descriptionTranslated })),
  ]);
}

// A category the taxonomy no longer lists still has a line or spending to show; it reads by its id rather than vanish.
export function budgetCategoryLabel(categories: BudgetCategory[], id: string | null): string {
  if (id === null) return UNCATEGORIZED_LABEL;
  return categories.find((category) => category.id === id)?.label ?? `Categoria ${id}`;
}

export interface BudgetDraftLine { key: string; categoryId: string; limit: string }
export interface BudgetDraft { limit: string; lines: BudgetDraftLine[] }

const formatCents = (cents: number): string => (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// "1.234,56" → 123456. The money input only ever produces this shape; anything else is no amount.
export function parseCents(formatted: string): number | null {
  const digits = formatted.replace(/\D/g, '');
  if (!digits) return null;
  const cents = Number.parseInt(digits, 10);
  return Number.isSafeInteger(cents) ? cents : null;
}

export function budgetDraft(budget: SpaceBudget): BudgetDraft {
  return { limit: budget.limitCents ? formatCents(budget.limitCents) : '', lines: budget.lines.map((line) => ({ key: line.categoryId, categoryId: line.categoryId, limit: formatCents(line.limitCents) })) };
}

// `fields` names the control each row's message is about, so only that control reads as invalid.
export type BudgetLineField = 'category' | 'limit';
export interface BudgetReview { errors: Record<string, string>; fields: Record<string, BudgetLineField>; request: SetSpaceBudgetRequest | null; dirty: boolean }

// What the form would send, or why it cannot. The API validates again (and owns the taxonomy rules: income and
// outside-the-budget categories are its to reject); this only catches what the person can fix without a round trip.
export function reviewBudgetDraft(draft: BudgetDraft, budget: SpaceBudget): BudgetReview {
  const errors: Record<string, string> = {};
  const fields: Record<string, BudgetLineField> = {};
  const fail = (key: string, field: BudgetLineField, message: string) => { errors[key] = message; fields[key] = field; };
  const seen = new Set<string>();
  const lines = draft.lines.map((line) => {
    const limitCents = parseCents(line.limit);
    if (!line.categoryId) fail(line.key, 'category', 'Escolha uma categoria.');
    else if (seen.has(line.categoryId)) fail(line.key, 'category', 'Essa categoria já tem um limite neste orçamento.');
    else if (!limitCents) fail(line.key, 'limit', 'Informe um limite maior que zero.');
    seen.add(line.categoryId);
    return { categoryId: line.categoryId, limitCents: limitCents ?? 0 };
  });
  if (lines.length > MAX_BUDGET_LINES) errors.lines = `Um orçamento pode ter até ${MAX_BUDGET_LINES} categorias.`;
  const limitCents = parseCents(draft.limit) || null;
  // Both directions: a draft that repeats one stored line in place of another has the same length and is not the same.
  const holds = (rows: SetSpaceBudgetRequest['lines'], line: SetSpaceBudgetRequest['lines'][number]) => rows.some((row) => row.categoryId === line.categoryId && row.limitCents === line.limitCents);
  const same = limitCents === (budget.limitCents || null) && lines.length === budget.lines.length
    && lines.every((line) => holds(budget.lines, line)) && budget.lines.every((line) => holds(lines, line));
  return { errors, fields, request: Object.keys(errors).length ? null : { limitCents, lines }, dirty: !same };
}

const isText = (value: unknown): value is string => typeof value === 'string';
// Whole, exactly representable cents: a fraction or an amount past 2^53 would be shown rounded, which is a wrong number.
const isCents = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

// Every field the page formats is checked: a 200 of another shape must read as a failed load, never throw.
export function isBudget(data: unknown): data is SpaceBudget {
  if (!isRecord(data) || !isText(data.periodKey) || !isCents(data.spentCents) || typeof data.canEdit !== 'boolean') return false;
  if (data.limitCents !== null && !isCents(data.limitCents)) return false;
  return Array.isArray(data.lines) && data.lines.every(isLine) && Array.isArray(data.unbudgeted) && data.unbudgeted.every(isUnbudgeted);
}

function isLine(row: unknown): boolean {
  // A line's limit is a divisor: zero would not be a limit at all, and the API never stores one.
  return isRecord(row) && isText(row.categoryId) && isCents(row.limitCents) && row.limitCents > 0 && isCents(row.spentCents);
}

function isUnbudgeted(row: unknown): boolean {
  return isRecord(row) && (row.categoryId === null || isText(row.categoryId)) && isCents(row.spentCents);
}
